/**
 * Courier / Big Move cargo catalogs + limits for Payers & Delivery.
 */

/** Soft ceiling for courier runs — above this, steer user to Big Move. */
export const COURIER_WEIGHT_LIMIT_KG = 30;

export const COURIER_ITEM_OPTIONS = [
  { id: 'documents', label: 'Documents / envelope' },
  { id: 'small_parcel', label: 'Small parcel (shoebox or smaller)' },
  { id: 'medium_parcel', label: 'Medium parcel' },
  { id: 'large_parcel', label: 'Large parcel' },
  { id: 'groceries', label: 'Groceries / food bags' },
  { id: 'takeaway', label: 'Food takeaway' },
  { id: 'electronics', label: 'Electronics (phone, laptop, small device)' },
  { id: 'clothing', label: 'Clothing / soft goods' },
  { id: 'pharmacy', label: 'Pharmacy / OTC (non-prescription)' },
  { id: 'flowers', label: 'Flowers / gifts' },
  { id: 'spare_parts', label: 'Spare parts (small)' },
  { id: 'keys_cards', label: 'Keys / cards / samples' },
  { id: 'other', label: 'Other (describe below)' },
];

export const COURIER_HANDLING_FLAGS = [
  { id: 'fragile', label: 'Fragile' },
  { id: 'perishable', label: 'Perishable / keep cool' },
  { id: 'signature', label: 'Signature on delivery' },
  { id: 'id_check', label: 'ID check at drop-off' },
];

export const BIG_MOVE_TYPE_OPTIONS = [
  { value: 'apartment', label: 'Apartment / house' },
  { value: 'office', label: 'Office relocation' },
  { value: 'furniture', label: 'Furniture / single bulky items' },
  { value: 'industrial', label: 'Industrial items' },
  { value: 'machinery', label: 'Machinery' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'commercial', label: 'Commercial goods' },
  { value: 'farm', label: 'Farm items' },
  { value: 'construction_material', label: 'Construction material' },
  { value: 'construction_disposal', label: 'Construction disposal' },
  { value: 'household_disposal', label: 'Household disposal' },
  { value: 'vehicles', label: 'Vehicle / ATV / equipment on trailer' },
  { value: 'other', label: 'Other' },
];

export const BIG_MOVE_CARGO_OPTIONS = [
  { id: 'furniture', label: 'Furniture' },
  { id: 'appliances', label: 'Appliances' },
  { id: 'industrial', label: 'Industrial items' },
  { id: 'machinery', label: 'Machinery' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'commercial', label: 'Commercial goods' },
  { id: 'farm', label: 'Farm items' },
  { id: 'construction_material', label: 'Construction material' },
  { id: 'construction_disposal', label: 'Construction disposal / debris' },
  { id: 'household_disposal', label: 'Household disposal / junk' },
  { id: 'office_equipment', label: 'Office equipment' },
  { id: 'pallets', label: 'Palletized goods' },
  { id: 'other', label: 'Other (describe below)' },
];

export const BIG_MOVE_ACCESS_OPTIONS = [
  { id: 'stairs', label: 'Stairs (no lift)' },
  { id: 'elevator', label: 'Elevator / lift' },
  { id: 'ground', label: 'Ground floor only' },
  { id: 'narrow', label: 'Narrow driveway / limited truck access' },
  { id: 'parking', label: 'Parking / loading bay available' },
];

export const BIG_MOVE_VEHICLE_OPTIONS = [
  { value: 'pickup', label: 'Pickup truck' },
  { value: 'van', label: 'Cargo van' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'box_truck', label: 'Box truck' },
  { value: 'unsure', label: 'Not sure — advise me' },
];

/** What the payer / proxy is funding — mirrors common buy/pay listing intents. */
export const PAYER_PURPOSE_OPTIONS = [
  { id: 'buy_new', label: 'Buy new item(s)' },
  { id: 'buy_used', label: 'Buy used / second-hand' },
  { id: 'buy_digital', label: 'Buy digital goods / license / top-up' },
  { id: 'bill_pay', label: 'Pay a bill (utility, school, fees)' },
  { id: 'online_transfer', label: 'Send / pay funds online only' },
  { id: 'rent_deposit', label: 'Pay rent / deposit' },
  { id: 'service_pay', label: 'Pay for a service' },
  { id: 'tickets', label: 'Tickets / bookings' },
  { id: 'pharmacy', label: 'Pharmacy / OTC' },
  { id: 'food', label: 'Food / restaurant order' },
  { id: 'collect_hold', label: 'Collect & pay (layaway / hold)' },
  { id: 'crypto_pay', label: 'Pay with locked digital assets / escrow' },
  { id: 'other', label: 'Other (describe below)' },
];

/** Category of goods/services — similar to marketplace listing categories. */
export const PAYER_CATEGORY_OPTIONS = [
  { id: 'electronics', label: 'Electronics' },
  { id: 'phones', label: 'Phones / accessories' },
  { id: 'appliances', label: 'Appliances' },
  { id: 'furniture', label: 'Furniture' },
  { id: 'clothing', label: 'Clothing / fashion' },
  { id: 'groceries', label: 'Groceries' },
  { id: 'pharmacy', label: 'Health / pharmacy' },
  { id: 'auto_parts', label: 'Auto parts' },
  { id: 'building', label: 'Building / hardware' },
  { id: 'farm', label: 'Farm / agricultural' },
  { id: 'office', label: 'Office / business supplies' },
  { id: 'bills', label: 'Bills / fees / government' },
  { id: 'events', label: 'Events / tickets' },
  { id: 'digital_goods', label: 'Digital goods / files' },
  { id: 'software', label: 'Software / licenses' },
  { id: 'gift_cards', label: 'Gift cards / airtime / top-ups' },
  { id: 'crypto', label: 'Crypto / digital assets' },
  { id: 'services', label: 'Professional / trade services' },
  { id: 'other', label: 'Other' },
];

export const PAYER_CONDITION_OPTIONS = [
  { value: 'new', label: 'New / sealed' },
  { value: 'like_new', label: 'Like new' },
  { value: 'used_good', label: 'Used — good' },
  { value: 'used_fair', label: 'Used — fair' },
  { value: 'na', label: 'N/A (bill / fee / service / digital)' },
];

export const PAYER_PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card (payer’s card if allowed)' },
  { value: 'transfer', label: 'Bank / wallet transfer at counter' },
  { value: 'mobile_wallet', label: 'Mobile money / e-wallet' },
  { value: 'stablecoin', label: 'Stablecoin (USDC / USDT)' },
  { value: 'crypto_wallet', label: 'Other crypto / digital asset' },
  { value: 'locked_escrow', label: 'Lock budget in non-custodial escrow' },
  { value: 'merchant_account', label: 'Pay into merchant account' },
  { value: 'any', label: 'Any accepted method' },
];

export const PAYER_PROOF_OPTIONS = [
  { id: 'photo_before', label: 'Photo of item before paying' },
  { id: 'photo_receipt', label: 'Photo of stamped receipt' },
  { id: 'video_call', label: 'Quick video / call confirmation' },
  { id: 'invoice', label: 'Invoice / account number on file' },
  { id: 'serial', label: 'Capture serial / IMEI' },
  { id: 'tx_hash', label: 'On-chain tx / escrow proof' },
  { id: 'digital_delivery', label: 'Confirm digital delivery / access' },
];

export const PAYER_SUBSTITUTION_OPTIONS = [
  { value: 'exact', label: 'Exact item only — no substitutes' },
  { value: 'same_brand', label: 'Same brand, similar model OK' },
  { value: 'similar', label: 'Similar item within budget OK' },
  { value: 'best_available', label: 'Best available — use judgment' },
];

export const PAYER_FULFILLMENT_OPTIONS = [
  { value: 'pay_leave', label: 'Pay only — leave goods at merchant' },
  { value: 'pay_collect', label: 'Pay and collect (hold for me)' },
  { value: 'pay_deliver', label: 'Pay, then deliver to me' },
  { value: 'bill_only', label: 'Bill payment only (no goods)' },
  { value: 'online_digital', label: 'Online only — digital goods / access' },
  { value: 'online_funds', label: 'Online only — funds transfer' },
  { value: 'escrow_release', label: 'Escrow release when I confirm receipt' },
];

export const DELIVERY_TERMS_SUMMARY =
  'I confirm this request contains only non-hazardous, legal items and materials. ' +
  'No explosives, illegal drugs, weapons, hazardous chemicals, biohazards, or other prohibited goods. ' +
  'I have read and agree to the marketplace terms for pay, prep, packing, and delivery runs. ' +
  'For payer jobs: the runner acts on my instructions within the stated budget; I accept responsibility for the purchase brief. ' +
  'Online-only and digital-asset jobs: I confirm lawful ownership of funds/assets and accurate transfer instructions.';

export const parseWeightKg = value => {
  const n = parseFloat(String(value || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

export const exceedsCourierWeightLimit = weightKg =>
  weightKg != null && weightKg > COURIER_WEIGHT_LIMIT_KG;

/** Demo runners for quote matching on Payer / Courier / Big Move. */
export const DEMO_PROXY_RUNNERS = [
  {
    id: 'rn-1',
    name: 'Kay · Kingston runs',
    categoryId: 'all',
    blurb: 'Payer + courier. Same-day.',
    etaHint: '~35 min',
    quoteNote: 'Can start after confirm. Escrow OK.',
  },
  {
    id: 'rn-2',
    name: 'Devon Delivery',
    categoryId: 'all',
    blurb: 'Portmore ↔ Kingston.',
    etaHint: '~50 min',
    quoteNote: 'Photo proof on every drop.',
  },
  {
    id: 'rn-3',
    name: 'BigLoad Crew',
    categoryId: 'all',
    blurb: 'Truck + 2 helpers.',
    etaHint: 'Within 24h',
    quoteNote: 'Best for Big Move / heavy.',
  },
];

export default {
  COURIER_WEIGHT_LIMIT_KG,
  COURIER_ITEM_OPTIONS,
  BIG_MOVE_TYPE_OPTIONS,
  BIG_MOVE_CARGO_OPTIONS,
  DEMO_PROXY_RUNNERS,
};
